import { createClient, type Provider, type SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "./client";
import { memoriaSessione } from "./memoriaSessione";

/**
 * L'accesso con Google o Apple DENTRO L'APP NATIVA, col flusso PKCE.
 *
 * IL BUCO (audit del 05/09/2026, Sol 2). Fino a quel giorno il ritorno
 * nell'app (`it.datacomtecnologie.ai://auth/callback#access_token=...`)
 * consegnava a `setSession` i token trovati nell'indirizzo, senza sapere se
 * quel giro l'avevamo cominciato noi. Chiunque poteva confezionare un link con
 * i token del PROPRIO account e farlo aprire a un installatore: il telefono
 * apriva l'app, e l'installatore si ritrovava dentro l'account dell'altro -
 * con le sue domande che finivano nella chat di uno sconosciuto.
 *
 * LA CURA: PKCE. Prima di aprire il browser l'app genera un segreto (il
 * «verificatore»), lo tiene nella propria memoria e manda a Supabase solo la
 * sua impronta. Al ritorno arriva un CODICE, non i token, e i token li da'
 * Supabase solo a chi presenta codice E verificatore insieme. Un link
 * confezionato da altri non ha il verificatore: non apre niente. Il vecchio
 * frammento con i token viene ignorato del tutto.
 *
 * PERCHE' UN SECONDO CLIENT. Il flusso (implicito o PKCE) si sceglie per
 * client, e il client principale deve restare implicito: i link delle email
 * (conferma, reset password) passano da /auth/v1/verify e tornano con i token
 * nel frammento; col PKCE quei link funzionerebbero solo nello stesso browser
 * che ha chiesto la mail, e un reset chiesto dall'app e aperto dal browser del
 * telefono morirebbe. Questo client serve SOLO al giro nativo con Google e
 * Apple; condivide la memoria del client principale, cosi' il verificatore e
 * la sessione stanno nello stesso posto, e a giro finito la sessione viene
 * consegnata al client principale con i token ricevuti da Supabase.
 */

export const SCHEMA_RITORNO = "it.datacomtecnologie.ai://auth/callback";

const URL_SUPABASE = import.meta.env.VITE_SUPABASE_URL || "http://configurazione-mancante.invalid";
const CHIAVE_PUBBLICA = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "configurazione-mancante";

let clientPkce: SupabaseClient | null = null;

function clientAccessoNativo(): SupabaseClient {
  if (!clientPkce) {
    clientPkce = createClient(URL_SUPABASE, CHIAVE_PUBBLICA, {
      auth: {
        storage: memoriaSessione(),
        persistSession: true,
        // Rinfresca il client principale: due orologi sulla stessa sessione
        // si pesterebbero i piedi.
        autoRefreshToken: false,
        detectSessionInUrl: false,
        flowType: "pkce",
      },
    });
  }
  return clientPkce;
}

/**
 * Comincia il giro: restituisce l'indirizzo da aprire nel browser di sistema.
 * Il verificatore resta nella memoria dell'app.
 */
export async function avviaAccessoNativo(provider: Provider): Promise<string> {
  const { data, error } = await clientAccessoNativo().auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: SCHEMA_RITORNO,
      // Il salto al browser lo facciamo noi: lasciar navigare la vista web
      // verso Google la porterebbe via dall'app.
      skipBrowserRedirect: true,
      // Solo Google: chiedere ad Apple di rimostrare il selettore non e'
      // previsto, e passargli parametri che non conosce fa fallire il giro.
      ...(provider === "google" ? { queryParams: { prompt: "select_account" } } : {}),
    },
  });
  if (error) throw error;
  if (!data?.url) throw new Error("Supabase non ha dato l'indirizzo di accesso");
  return data.url;
}

/** Il codice PKCE nell'indirizzo di ritorno, o null. I frammenti (#...) non contano. */
export function codiceDiRitorno(url: string): string | null {
  const senzaFrammento = url.split("#")[0];
  const query = senzaFrammento.split("?")[1] ?? "";
  const codice = new URLSearchParams(query).get("code");
  return codice && codice.trim() ? codice.trim() : null;
}

/**
 * Finisce il giro quando il telefono riapre l'app: scambia il codice con la
 * sessione e la consegna al client principale. "rifiutato" = si atterra sul
 * login, senza aver toccato niente.
 */
export async function completaAccessoNativo(url: string): Promise<"ok" | "rifiutato"> {
  const codice = codiceDiRitorno(url);
  if (!codice) {
    // Niente codice: o l'utente ha annullato, o qualcuno ha provato il
    // vecchio trucco dei token nel frammento. In tutt'e due i casi, no.
    console.warn("accesso_nativo_senza_codice");
    return "rifiutato";
  }
  const { data, error } = await clientAccessoNativo().auth.exchangeCodeForSession(codice);
  if (error || !data?.session) {
    console.warn("accesso_nativo_scambio_fallito", error?.message);
    return "rifiutato";
  }
  const { error: errSessione } = await supabase.auth.setSession({
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
  });
  if (errSessione) {
    console.warn("accesso_nativo_sessione_rifiutata", errSessione.message);
    return "rifiutato";
  }
  return "ok";
}
